import { type ConnectOptions, type Event as QZEvent } from "qz-tray";

import { qz } from "../client";
import {
  classifyCloseEvent,
  classifyConnectError,
  readCloseInfo,
} from "./errors";
import { resetAllNamespaces } from "./registry";
import { type Listener, type QZState } from "./types";

const listeners = new Set<Listener>();

let initialized = false;
let connectPromise: Promise<void> | null = null;
let hasBeenConnected = false;

let state: QZState = {
  connected: false,
  connecting: false,
  lastCloseInfo: null,
  lastError: null,
  lastErrorCode: null,
};

const emit = () => {
  for (const listener of listeners) listener();
};

const setState = (updater: (state: QZState) => QZState) => {
  state = updater(state);
  emit();
};

const onClose = (event: QZEvent) => {
  const info = readCloseInfo(event);
  const code = classifyCloseEvent(info);
  resetAllNamespaces();
  setState((s) => ({
    ...s,
    connected: false,
    connecting: false,
    lastCloseInfo: info,
    lastError: info.wasClean
      ? null
      : info.reason && info.reason.length > 0
      ? info.reason
      : "Connection closed",
    lastErrorCode: code,
  }));
};

const onError = (_event: QZEvent) => {
  // During `findConnection` port probing, `onerror` fires right before
  // `onclose`. Let `onClose` own the final state (it carries `wasClean`,
  // `code`, and `reason`) and avoid overwriting a previously clean close
  // with a noisy "Connection error" string.
};

const init = () => {
  if (initialized) return;

  initialized = true;
  qz.websocket.setClosedCallbacks(onClose);
  qz.websocket.setErrorCallbacks(onError);
};

export const subscribe = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getSnapshot = () => state;

export const connect = async (options?: ConnectOptions) => {
  init();

  if (state.connected) return;

  if (connectPromise) return connectPromise;

  setState((s) => ({
    ...s,
    connecting: true,
    lastError: null,
    lastErrorCode: null,
  }));

  connectPromise = qz.websocket
    .connect(options ?? { delay: 1, keepAlive: 60, retries: 3 })
    .then(() => {
      hasBeenConnected = true;
      resetAllNamespaces();
      setState((s) => ({
        ...s,
        connected: true,
        connecting: false,
        lastCloseInfo: null,
        lastError: null,
        lastErrorCode: null,
      }));
    })
    .catch((err: unknown) => {
      const code = classifyConnectError(err, hasBeenConnected);
      setState((s) => ({
        ...s,
        connected: false,
        connecting: false,
        lastError: err instanceof Error ? err.message : String(err),
        lastErrorCode: code,
      }));
      throw err;
    })
    .finally(() => {
      connectPromise = null;
    });

  return connectPromise;
};

export const disconnect = async () => {
  await qz.websocket.disconnect();
};
